import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  cartData,
  ShopifyCart,
  addToCart,
  publishKlaviyoAddedToCart,
  deletedVariantIds,
  addToDeletedVariants,
} from "../store/cart-store";
import {
  getCookie,
  handleize,
  getProductByHandle,
  formatToDollarCurrencyString,
} from "../utils/knocking";

interface CrossSellingListItem {
  imageUrl?: string;
  productHandle?: string;
  /** Product title — used to derive a handle via handleize() when productHandle is absent */
  product?: string;
  /** GID format: gid://shopify/ProductVariant/123 */
  variantId?: string;
  /** Numeric variant ID — fallback when variantId is absent */
  variant?: number;
}

function parseVariantGid(gid: string): number {
  return parseInt(gid.replace("gid://shopify/ProductVariant/", ""), 10);
}

/** Returns a new array with the elements Fisher-Yates shuffled. */
function shuffle<T>(items: T[]): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * <cross-selling-cart>
 *
 * Reactive Lit replacement for the legacy CrossSellingCartComponent.
 * Subscribes to cartData and deletedVariantIds signals; finds the first
 * eligible cross-sell variant and renders it as a slide-up banner inside
 * the cart drawer footer.
 *
 * Attributes (set in Liquid):
 *   data-holiday-shipping  — "true" | "false", controls Ships-by property
 */
@customElement("cross-selling-cart")
export class CrossSellingCart extends LitElement {
  @state() private _title = "";
  @state() private _variantTitle = "";
  @state() private _imageUrl = "";
  @state() private _price = 0;
  @state() private _compareAtPrice: number | null = null;
  @state() private _productUrl = "";
  @state() private _visible = false;
  @state() private _adding = false;

  private _variantId: number | null = null;
  private _productId: number | null = null;
  private _shipsByRaw: string | null = null;
  private _crossSellingList: CrossSellingListItem[] = [];
  private _holidayShipping = false;
  private _randomSelection = false;
  /** Incremented each time _findAndLoad is called; lets in-flight searches self-cancel. */
  private _findSeq = 0;
  /** Set to true when the user clicks the close button. Hides the entire banner for the current page. */
  private _bannerClosed = false;
  private _disposeEffect?: () => void;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this._crossSellingList = (window as any).crossSellingVariantsJson ?? [];
    this._holidayShipping = this.dataset.holidayShipping === "true";
    this._randomSelection = this.dataset.randomSelection === "true";

    this._disposeEffect = effect(() => {
      // If the user closed the banner on this page, keep it hidden permanently.
      if (this._bannerClosed) {
        this._visible = false;
        return;
      }

      const cart = cartData.value;
      deletedVariantIds.value; // subscribe so effect re-runs on dismiss
      if (cart !== null) {
        this._findAndLoad(cart);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
  }

  updated() {
    // Actually hide/show the banner using Tailwind.
    this.classList.toggle("hidden", !this._visible);
    this.toggleAttribute("inert", !this._visible);
    this.setAttribute("aria-hidden", String(!this._visible));

    const inner = document.querySelector(".cart-drawer__inner-content");
    if (inner) inner.classList.toggle("has-cross-selling", this._visible);

    // Recalculate footer height so the item list scrolls correctly.
    const footer = document.getElementById("cartDrawerFooter");
    const cartInner = document.getElementById("cartDrawerInner");
    if (footer && cartInner) {
      cartInner.style.bottom = `${footer.offsetHeight}px`;
    }
  }

  private async _findAndLoad(cart: ShopifyCart): Promise<void> {
    const seq = ++this._findSeq;
    const variantIdsInCart = cart.items.map((item) => item.variant_id);
    const deleted = deletedVariantIds.value;

    // When random selection is enabled, shuffle a copy of the list so the first
    // eligible candidate is picked at random rather than always the list's head.
    // Shuffling up front preserves the early-exit on the async availability
    // checks below (no need to fetch every product to pick a random one).
    const list = this._randomSelection
      ? shuffle(this._crossSellingList)
      : this._crossSellingList;

    for (const item of list) {
      const variantId = item.variantId
        ? parseVariantGid(item.variantId)
        : (item.variant ?? null);

      if (variantId === null) continue;
      if (variantIdsInCart.includes(variantId)) continue;
      if (deleted.includes(variantId)) continue;

      const handle =
        item.productHandle ?? (item.product ? handleize(item.product) : null);
      if (!handle) continue;

      const product = await getProductByHandle(handle);

      // A newer _findAndLoad call started while we were awaiting — bail out.
      if (seq !== this._findSeq) return;

      if (!product) continue;

      const isCustomizable = product.tags.some(
        (tag) => tag.includes("customtext") || tag.includes("custom-sku")
      );
      if (isCustomizable) continue;

      const variant = product.variants.find((v) => v.id === variantId);
      if (!variant?.available) continue;

      // Valid candidate found — populate state and show.
      let imageUrl = item.imageUrl ?? "";
      if (!imageUrl && variant.featured_image) imageUrl = variant.featured_image.src;
      if (!imageUrl) imageUrl = product.featured_image;

      this._variantId = variant.id;
      this._productId = product.id;
      // Mirror product-store / collection-store: use the product's `ships_by-`
      // tag when present, otherwise fall back to the `defaultShippingDate` theme
      // setting. Without the fallback, cross-sell items lost the ships-by that
      // the product page and cards apply to the same product.
      const shippingTag = product.tags.find((t) => t.startsWith("ships_by-"));
      const fallbackShipsBy = (window as any).defaultShippingDate as string | undefined;
      this._shipsByRaw = shippingTag
        ? shippingTag.replace("ships_by-", "")
        : (fallbackShipsBy || null);
      this._title = product.title;
      this._variantTitle = variant.title === "Default Title" ? "" : variant.title;
      this._imageUrl = imageUrl;
      this._price = variant.price;
      this._compareAtPrice = variant.compare_at_price;
      this._productUrl = `/products/${product.handle}?variant=${variant.id}`;
      this._visible = true;
      return;
    }

    // No valid candidate — hide.
    this._visible = false;
    this._variantId = null;
  }

  private _onClose(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    // Cancel any in-flight _findAndLoad so it can't re-show the banner.
    ++this._findSeq;

    // Permanently hide the banner for the current page.
    this._bannerClosed = true;
    this._visible = false;

    if (this._variantId !== null) {
      // Persist the discarded variant id so it is excluded on future pages
      // in the same tab session.
      addToDeletedVariants(this._variantId);
    }
  }

  private async _onAdd() {
    if (this._variantId === null) return;
    this._adding = true;
    try {
      const properties: Record<string, any> = {
        "_promo-code": getCookie("promo_code"),
      };
      if (this._shipsByRaw) {
        properties["_ships-by"] = this._shipsByRaw;
      }
      const added = await addToCart(this._variantId, 1, properties, undefined, {
        source: "cross-selling-cart",
        productId: this._productId ?? undefined,
      });
      if (added) {
        // `kn_add_to_cart` already fired inside executeCartAdd (per line).
        // Klaviyo's whole-cart event fires once here for the user action.
        publishKlaviyoAddedToCart();
      }
      this._visible = false;
      this._scrollCartToShowNewItem();
    } finally {
      this._adding = false;
    }
  }

  private _scrollCartToShowNewItem() {
    const scrollArea = document.getElementById("cartScrollArea");
    if (!scrollArea) return;
    const scrollToTop = () => {
      scrollArea.scrollTo({ top: 0, behavior: "smooth" });
    };
    requestAnimationFrame(() => {
      setTimeout(scrollToTop, 350);
    });
  }

  render() {
    return html`
      <div class="bg-[var(--color-primary-50)] p-[15px] flex w-full gap-2.5 items-center relative">
        <button
          type="button"
          class="cross-selling__close-button absolute top-[10px] right-[10px] w-[12px] h-[12px] flex items-center justify-center p-0 [&_svg]:w-[12px] [&_svg]:h-[12px]"
          aria-label="Close suggestion"
          @click=${this._onClose}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
            role="presentation"
            viewBox="0 0 18 17"
            fill="none"
            class="icon icon-close"
          >
            <path
              d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z"
              fill="currentColor"
            />
          </svg>
        </button>

        <div class="ajaxcart__product-image">
          <a href="${this._productUrl}">
            <img class="w-[95px] h-[95px] object-contain rounded" src="${this._imageUrl}" alt="${this._title}" />
          </a>
        </div>

        <div class="ajaxcart__product-content flex flex-col gap-2.5 flex-1 min-w-0">
          <div>
            <div class="text-caption font-bold text-[var(--color-primary-950)]">You might also like</div>
            <a href="${this._productUrl}">
              <div class="text-sm font-bold my-[5px]">${this._title}</div>
              ${this._variantTitle
        ? html`<div class="variant-label small">${this._variantTitle}</div>`
        : ""}
            </a>
          </div>

          <div class="ajaxcart__product-add flex gap-2.5 align-center justify-between">
            <div class="range flex items-center gap-1.5">
              <span class="ajaxcart__price text-[var(--color-primary-950)] font-bold">
                ${this._price ? formatToDollarCurrencyString(this._price) : ""}
              </span>
              ${this._compareAtPrice
        ? html`<s class="ajaxcart__price text-xs font-medium text-[var(--color-secondary-800)]">
                    ${formatToDollarCurrencyString(this._compareAtPrice)}
                  </s>`
        : ""}
            </div>
            <button
              class="btn size-auto p-[10px] text-xs font-bold text-white py-[6px] px-[8px] rounded"
              type="button"
              ?disabled=${this._adding}
              @click=${this._onAdd}
            >
              ${this._adding ? "Adding…" : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cross-selling-cart": CrossSellingCart;
  }
}
